import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { signInPost, signOutPost } from '@/services/api/requests/user';
import { TUser } from '@/types/User.types';
import roles from '@/constants/roles';
import { useAppStore } from '@/store/useAppStore';
import { deriveAuthFlags } from './deriveAuth';
import { bootSession } from './bootSession';
import { applySessionBundle } from './sessionBundle';
import LoadingPage from '@/components/templates/LoadingPage';
import AccountUnavailablePage from '@/components/templates/AccountUnavailablePage';
import { isUserAllowedOnThisPlatform, WRONG_PLATFORM_MESSAGE } from './platformAccess';
import { endSession } from '@/services/endSession';
import { getMemberCareSpecialistOwnProfile } from '@/services/api/requests/member-care';

export type TAuthContext = {
  user: TUser | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isMemberCare: boolean;
  isOnboarded: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

export const AuthContext = createContext<TAuthContext | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, setUser, setMemberCare } = useAppStore();

  const { isAuthenticated, isSuperAdmin, isMemberCare, isOnboarded } = deriveAuthFlags(user);

  const handleUserDataFlow = async (userData: TUser) => {
    if (!isUserAllowedOnThisPlatform(userData)) {
      // One rejection channel, not two: the thrown message is what the sign-in
      // form renders. A toast alongside it could disagree with the inline error
      // and outlive the page it belongs to.
      await signOut();

      throw Error(WRONG_PLATFORM_MESSAGE);
    }

    // Fetch before committing anything. setUser used to run first, so a failed
    // profile fetch left the store holding a persisted user with memberCare null
    // while the form showed an error — a half-signed-in state that the boot check
    // does not catch, because it only blocks a missing slice when the persisted
    // user is a *different* person.
    const memberCare = userData.roles.includes(roles.memberCare)
      ? (await getMemberCareSpecialistOwnProfile()).data
      : null;

    setUser(userData);
    if (memberCare) setMemberCare(memberCare);
  };

  const signIn = useCallback(async (email: string, password: string) => {
    const userData = await signInPost(email, password);

    await handleUserDataFlow(userData);
  }, []);

  const signOut = useCallback(async () => {
    // endSession first: it navigates away *before* clearing state, so the
    // authenticated tree unmounts instead of re-rendering against an emptied
    // store, and it clears the query cache too — which resetting the stores alone
    // never did, so a previous user's cached tables survived into the next
    // session. Then the logout request, fire-and-forget: awaiting it meant a
    // failed logout skipped the reset entirely and left the user signed in
    // locally with a dead server session.
    await endSession();

    signOutPost().catch(() => {});
  }, []);

  // Boot-time session check. The persisted user is an optimistic hint, not proof:
  // nothing verified the cookie, so a stale localStorage entry used to route the
  // user into the authenticated shell, fire the loaders, collect 401s and only
  // then bounce them out. Verify once, before the router mounts.
  const [bootState, setBootState] = useState<'checking' | 'ready' | 'unavailable'>(() =>
    // Nothing persisted means nobody to verify; a signed-out visitor should not
    // wait on a request that is certain to fail.
    useAppStore.getState().user ? 'checking' : 'ready',
  );
  const [bootAttempt, setBootAttempt] = useState(0);

  useEffect(() => {
    if (bootState !== 'checking') return;

    let cancelled = false;

    void (async () => {
      const result = await bootSession();

      if (cancelled) return;

      switch (result.status) {
        case 'authenticated':
          applySessionBundle(result.bundle);
          setBootState('ready');
          break;
        case 'signed-out':
          await endSession({ reason: 'expired' });
          break;
        case 'wrong-platform':
          await endSession({ reason: 'wrong-platform' });
          break;
        case 'unavailable':
          // Valid session, unreadable data. Neither render nor sign out.
          setBootState('unavailable');
          break;
      }
    })();

    return () => {
      cancelled = true;
    };
    // StrictMode double-invokes this in development; the cancelled flag makes the
    // first pass a no-op rather than letting two checks race.
  }, [bootState, bootAttempt]);

  const auth = useMemo(() => {
    return {
      isAuthenticated,
      user,
      isSuperAdmin,
      isMemberCare,
      isOnboarded,
      signIn,
      signOut,
    };
  }, [isAuthenticated, user, signIn, signOut, isSuperAdmin, isMemberCare, isOnboarded]);

  if (bootState === 'checking') return <LoadingPage />;

  if (bootState === 'unavailable') {
    return (
      <AccountUnavailablePage
        onRetry={() => {
          setBootAttempt((attempt) => attempt + 1);
          setBootState('checking');
        }}
        onSignOut={() => void endSession()}
      />
    );
  }

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
